import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { ArrowLeft, Camera, Upload, Loader2, Mic, MicOff, Send, X, ImageIcon, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

export default function CameraDoubt() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState('');
  const [question, setQuestion] = useState('');
  const [solution, setSolution] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const textareaRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const canSubmit = (imageBase64 || question.trim()) && !loading;

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [question]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
      setImageBase64(reader.result.split(',')[1]);
      setSolution('');
    };
    reader.readAsDataURL(file);
  };

  const captureFromCamera = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = handleImageUpload;
    input.click();
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageBase64('');
    setSolution('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const solveProblem = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setSolution('');
    try {
      const payload = { question_text: question.trim() || 'Please solve this math problem step by step.' };
      if (imageBase64) payload.image_base64 = imageBase64;
      const res = await axios.post(`${API}/student/camera-doubt`, payload, { headers, withCredentials: true });
      setSolution(res.data.solution);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to analyze. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (chunksRef.current.length === 0) {
          toast.error('No audio recorded');
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size < 100) {
          toast.error('Recording too short. Please try again.');
          return;
        }
        setTranscribing(true);
        try {
          const reader = new FileReader();
          const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
          });
          reader.readAsDataURL(blob);
          const audioBase64 = await base64Promise;

          const res = await axios.post(`${API}/student/voice-to-text`, {
            audio_base64: audioBase64
          }, { headers, withCredentials: true });

          const text = res.data.text?.trim();
          if (text) {
            setQuestion(prev => {
              const newVal = prev ? prev + ' ' + text : text;
              return newVal;
            });
            toast.success(`Transcribed: "${text.substring(0, 40)}${text.length > 40 ? '...' : ''}"`);
          } else {
            toast.error('Could not understand audio. Please speak clearly and try again.');
          }
        } catch (e) {
          toast.error('Voice transcription failed. Please try again.');
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorder.start(100);
      setRecording(true);
      toast.info('Recording... tap mic again to stop');
    } catch {
      toast.error('Microphone access denied. Please allow mic access in your browser settings.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const renderMath = (text) => {
    if (!text) return null;
    try {
      const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[^$]+?\$)/g);
      return parts.map((p, i) => {
        if (p.startsWith('$$') && p.endsWith('$$')) return <BlockMath key={i} math={p.slice(2, -2)} />;
        if (p.startsWith('$') && p.endsWith('$')) return <InlineMath key={i} math={p.slice(1, -1)} />;
        return <span key={i}>{p}</span>;
      });
    } catch { return text; }
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate('/home')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-[#1B4F8A]" />
        </button>
        <h1 className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>Snap & Solve</h1>
      </div>

      <div className="px-4 space-y-3">
        {/* Mode indicator */}
        <div className="flex gap-2 items-center text-xs">
          <span className="px-2 py-1 rounded-full font-bold flex items-center gap-1"
            style={{ background: imageBase64 ? '#EDE9FE' : '#F0F9FF', color: imageBase64 ? '#8338EC' : '#1B4F8A' }}>
            {imageBase64 ? <><ImageIcon className="w-3 h-3" /> Image attached</> : <><MessageSquare className="w-3 h-3" /> Type or speak your doubt</>}
          </span>
        </div>

        {/* Image upload area */}
        {!imagePreview ? (
          <div data-testid="camera-upload-area"
            className="bg-white border-2 border-dashed rounded-2xl p-6 text-center"
            style={{ borderColor: '#D4C5B9' }}>
            <Camera size={36} className="mx-auto mb-2 text-[#8338EC]" />
            <p className="text-sm font-bold" style={{ color: '#1B4F8A' }}>Upload a photo of your math problem</p>
            <p className="text-xs mt-1 mb-3" style={{ color: '#8B7355' }}>Or just type your question below — image is optional!</p>
            <div className="flex gap-2 justify-center">
              <Button data-testid="camera-capture-btn" onClick={captureFromCamera}
                className="bg-[#8338EC] hover:bg-[#7028D6] text-white font-bold rounded-full text-xs px-4 h-8">
                <Camera className="w-3 h-3 mr-1" /> Camera
              </Button>
              <Button data-testid="camera-gallery-btn" onClick={() => fileInputRef.current?.click()}
                variant="outline" className="font-bold rounded-full text-xs px-4 h-8 border-[#8338EC] text-[#8338EC]">
                <Upload className="w-3 h-3 mr-1" /> Gallery
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white border-2 rounded-2xl overflow-hidden relative" style={{ borderColor: '#8338EC' }}>
            <img src={imagePreview} alt="Problem" className="w-full max-h-52 object-contain bg-gray-50" data-testid="camera-preview" />
            <button onClick={removeImage} data-testid="remove-image-btn"
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

        {/* Question input with voice + send */}
        <div className="bg-white border-2 rounded-2xl p-3" style={{ borderColor: '#E5DACE' }}>
          <label className="text-xs font-bold mb-1.5 block" style={{ color: '#8B7355' }}>
            Your question {imageBase64 ? '(optional — describe what to solve)' : '(type your math doubt here)'}
          </label>
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              data-testid="camera-question-input"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              rows={1}
              className="flex-1 text-sm p-2.5 border rounded-xl resize-none overflow-hidden"
              style={{ borderColor: '#E5DACE', color: '#1B4F8A', minHeight: '40px', maxHeight: '120px' }}
              placeholder={imageBase64 ? 'e.g., Solve step by step / Explain this formula' : 'e.g., How to find HCF of 12 and 18?'}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && canSubmit) { e.preventDefault(); solveProblem(); } }}
            />
            <div className="flex flex-col gap-1.5">
              <button data-testid="voice-input-btn"
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                className="w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all shrink-0"
                style={{
                  borderColor: recording ? '#E63946' : '#8338EC',
                  background: recording ? '#E63946' : transcribing ? '#F3F0FF' : 'white',
                  animation: recording ? 'pulse 1.5s ease-in-out infinite' : 'none'
                }}>
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin text-[#8338EC]" /> :
                  recording ? <MicOff size={14} color="white" /> : <Mic size={14} color="#8338EC" />}
              </button>
              <button data-testid="solve-doubt-btn"
                onClick={solveProblem}
                disabled={!canSubmit}
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all shrink-0"
                style={{
                  background: canSubmit ? '#FF6B35' : '#E5DACE',
                  cursor: canSubmit ? 'pointer' : 'not-allowed'
                }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> :
                  <Send size={14} color="white" />}
              </button>
            </div>
          </div>
          {recording && (
            <div className="mt-2 flex items-center gap-2 text-xs font-bold text-[#E63946]" data-testid="recording-indicator">
              <span className="w-2 h-2 rounded-full bg-[#E63946] animate-pulse" />
              Recording... tap mic to stop
            </div>
          )}
          {transcribing && (
            <div className="mt-2 flex items-center gap-2 text-xs font-bold text-[#8338EC]" data-testid="transcribing-indicator">
              <Loader2 className="w-3 h-3 animate-spin" />
              Transcribing your voice...
            </div>
          )}
        </div>

        {/* Solution */}
        {loading && (
          <div className="bg-white border-2 rounded-2xl p-5 text-center" style={{ borderColor: '#E5DACE' }}>
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6B35] mx-auto mb-2" />
            <p className="text-sm font-bold text-[#1B4F8A]">Zeno is solving...</p>
            <p className="text-xs text-[#8B7355] mt-1">This may take a few seconds</p>
          </div>
        )}

        {solution && (
          <div data-testid="camera-solution" className="bg-white border-2 rounded-2xl p-4 space-y-2" style={{ borderColor: '#06D6A0' }}>
            <p className="text-xs font-bold" style={{ color: '#06D6A0' }}>ZENO'S SOLUTION</p>
            <div className="text-sm leading-relaxed prose prose-sm max-w-none" style={{ color: '#1B4F8A' }}>
              {solution.split('\n').map((line, i) => (
                <p key={i} className="mb-1">{renderMath(line)}</p>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(230,57,70,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(230,57,70,0); }
        }
      `}</style>
      <BottomNav active="home" />
    </div>
  );
}
